/**
 * Vivotek ND-series + Mobotix MOVE NVR catalog.
 *
 * Verified against vivotek.com / vivotek.vn product pages and PDF
 * datasheets + mobotix.com/en/MOVE-NVR PDF datasheets on 2026-05-15.
 *
 * Extra fields from the source JSON not in the NVRSpec schema
 * (`orderCode`, `dimensionsMm`, `powerConsumptionW`) were dropped.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const VIVOTEK_MOBOTIX_NVR_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // Vivotek ND-series — Core+ embedded NVRs with PoE
  // ─────────────────────────────────────────────────────────────

  {
    id: 'vivotek-nd9213p',
    manufacturer: 'Vivotek',
    model: 'ND9213P',
    category: 'nvr-recorder',
    tagline: 'H.265 4-CH embedded NVR with 4-port PoE and 4K display',
    verified: true,
    datasheetUrl: 'https://www.vivotek.com/en-US/products/network_video_recorder_and_appliance/core+_series_nvr/nd9213p',
    specs: {
      channels: 4,
      inputBandwidthMbps: 64,
      outputBandwidthMbps: 88,
      maxResolution: '8 MP',
      hddBays: 1,
      poeInPorts: 4,
      poeBudgetW: 50,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100 RJ45',
    },
  },

  {
    id: 'vivotek-nd9322p',
    manufacturer: 'Vivotek',
    model: 'ND9322P',
    category: 'nvr-recorder',
    tagline: 'H.265 8-CH embedded NVR with 8-port PoE and 2-bay RAID storage',
    verified: true,
    datasheetUrl: 'https://www.vivotek.com/en-US/products/network_video_recorder_and_appliance/core+_series_nvr/nd9322p',
    specs: {
      channels: 8,
      inputBandwidthMbps: 64,
      outputBandwidthMbps: 24,
      maxResolution: '4K',
      hddBays: 2,
      hddBayMaxTb: 8,
      poeInPorts: 8,
      poeBudgetW: 120,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      raidLevels: ['0', '1'],
      formFactor: 'desktop',
      networkPorts: '1× 1GbE RJ45',
    },
  },

  {
    id: 'vivotek-nd9326p',
    manufacturer: 'Vivotek',
    model: 'ND9326P',
    category: 'nvr-recorder',
    tagline: 'Core+ AI H.265 8-CH NVR with 8-port PoE, Deep Search and 4K display',
    verified: true,
    datasheetUrl: 'https://www.vivotek.com/products/network_video_recorder_and_appliance/core+_ai_series_nvr/nd9326p',
    specs: {
      channels: 8,
      inputBandwidthMbps: 192,
      outputBandwidthMbps: 224,
      maxResolution: '4K',
      hddBays: 2,
      poeInPorts: 8,
      poeBudgetW: 120,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      raidLevels: ['0', '1'],
      formFactor: 'desktop',
      networkPorts: '2× 1GbE RJ45',
    },
  },

  {
    id: 'vivotek-nd9424p',
    manufacturer: 'Vivotek',
    model: 'ND9424P',
    category: 'nvr-recorder',
    tagline: 'H.265 16-CH embedded NVR with 16-port PoE and 2-bay RAID storage',
    verified: true,
    datasheetUrl: 'https://www.vivotek.com/en-US/products/network_video_recorder_and_appliance/core+_series_nvr/nd9424p',
    specs: {
      channels: 16,
      inputBandwidthMbps: 96,
      outputBandwidthMbps: 24,
      maxResolution: '4K',
      hddBays: 2,
      hddBayMaxTb: 8,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      raidLevels: ['0', '1'],
      formFactor: 'desktop',
      networkPorts: '1× 1GbE RJ45',
    },
  },

  {
    id: 'vivotek-nd9425p',
    manufacturer: 'Vivotek',
    model: 'ND9425P',
    category: 'nvr-recorder',
    tagline: 'H.265 16-CH embedded NVR with 16-port PoE and 2-bay RAID storage',
    verified: true,
    datasheetUrl: 'https://www.vivotek.com/en-US/products/network_video_recorder_and_appliance/core+_series_nvr/nd9425p',
    specs: {
      channels: 16,
      inputBandwidthMbps: 64,
      outputBandwidthMbps: 88,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      raidLevels: ['0', '1'],
      formFactor: 'desktop',
      networkPorts: '1× 1GbE RJ45',
    },
  },

  {
    id: 'vivotek-nd9441p',
    manufacturer: 'Vivotek',
    model: 'ND9441P',
    category: 'nvr-recorder',
    tagline: 'H.265 16-CH NVR with 16-port PoE, 4-bay RAID and 12 MP camera support',
    verified: true,
    datasheetUrl: 'https://www.vivotek.com/en-US/products/network_video_recorder_and_appliance/core+_series_nvr/nd9441p',
    specs: {
      channels: 16,
      inputBandwidthMbps: 192,
      outputBandwidthMbps: 96,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 8,
      poeInPorts: 16,
      poeBudgetW: 160,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      raidLevels: ['0', '1', '5'],
      formFactor: 'desktop',
      networkPorts: '2× 1GbE RJ45',
    },
  },

  {
    id: 'vivotek-nd9442p',
    manufacturer: 'Vivotek',
    model: 'ND9442P',
    category: 'nvr-recorder',
    tagline: 'Core+ AI 16-CH NVR with 16-port PoE, 4-bay RAID and Deep Search',
    verified: true,
    datasheetUrl: 'https://www.vivotek.com/products/network_video_recorder_and_appliance/core+_ai_series_nvr/nd9442p',
    specs: {
      channels: 16,
      inputBandwidthMbps: 192,
      outputBandwidthMbps: 224,
      maxResolution: '20 MP',
      hddBays: 4,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      raidLevels: ['0', '1', '5'],
      formFactor: 'desktop',
      networkPorts: '2× 1GbE RJ45',
    },
  },

  {
    id: 'vivotek-nd9541p',
    manufacturer: 'Vivotek',
    model: 'ND9541P',
    category: 'nvr-recorder',
    tagline: 'H.265 32-CH NVR with 16-port PoE, 4-bay RAID and 12 MP camera support',
    verified: true,
    datasheetUrl: 'https://www.vivotek.com/en-US/products/network_video_recorder_and_appliance/core+_series_nvr/nd9541p',
    specs: {
      channels: 32,
      inputBandwidthMbps: 192,
      outputBandwidthMbps: 96,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 8,
      poeInPorts: 16,
      poeBudgetW: 160,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      raidLevels: ['0', '1', '5'],
      formFactor: 'desktop',
      networkPorts: '2× 1GbE RJ45',
    },
  },

  {
    id: 'vivotek-nd9542p',
    manufacturer: 'Vivotek',
    model: 'ND9542P',
    category: 'nvr-recorder',
    tagline: 'Core+ AI 32-CH NVR with 16-port PoE, 4-bay RAID and Deep Search',
    verified: true,
    datasheetUrl: 'https://www.vivotek.com/products/network-video-recorder-and-appliance/core+-ai-series-nvr/nd9542p',
    specs: {
      channels: 32,
      inputBandwidthMbps: 192,
      outputBandwidthMbps: 224,
      maxResolution: '4K',
      hddBays: 4,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      raidLevels: ['0', '1', '5'],
      formFactor: 'desktop',
      networkPorts: '2× 1GbE RJ45',
    },
  },

  {
    id: 'vivotek-nr9581-v3',
    manufacturer: 'Vivotek',
    model: 'NR9581-V3',
    category: 'nvr-recorder',
    tagline: 'H.265 32-CH 2U recording server with 8 hot-swap bays, dual PSU and VAST Security Station',
    verified: true,
    datasheetUrl: 'https://www.vivotek.com/en-US/products/network_video_recorder_and_appliance/recording_server/nr9581-v3',
    specs: {
      channels: 32,
      inputBandwidthMbps: 512,
      outputBandwidthMbps: 650,
      hddBays: 8,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10'],
      formFactor: '2U',
      networkPorts: '2× 2.5GbE RJ45',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Mobotix MOVE NVR — current desktop + 2U rackmount lineup
  // ─────────────────────────────────────────────────────────────

  {
    id: 'mobotix-move-nvr-8b',
    manufacturer: 'Mobotix',
    model: 'MOVE NVR-8B (Mx-S-NVR1B-8-POE)',
    category: 'nvr-recorder',
    tagline: 'Plug-and-Play 8-CH NVR with 8-port PoE, 2-bay RAID and 4K camera support',
    verified: true,
    datasheetUrl: 'https://www.mobotix.com/en/MOVE-NVR',
    specs: {
      channels: 8,
      inputBandwidthMbps: 120,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 8,
      poeBudgetW: 120,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '2× 1GbE RJ45 (WAN + LAN uplink)',
    },
  },

  {
    id: 'mobotix-move-nvr-16b',
    manufacturer: 'Mobotix',
    model: 'MOVE NVR-16B (Mx-S-NVR1B-16-POE)',
    category: 'nvr-recorder',
    tagline: 'Plug-and-Play 16-CH NVR with 16-port PoE, 2-bay RAID and 4K camera support',
    verified: true,
    datasheetUrl: 'https://www.mobotix.com/en/MOVE-NVR',
    specs: {
      channels: 16,
      maxResolution: '12 MP',
      hddBays: 2,
      poeInPorts: 16,
      poeBudgetW: 140,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '2× 1GbE RJ45 (WAN + LAN uplink)',
    },
  },

  {
    id: 'mobotix-move-nvr-16',
    manufacturer: 'Mobotix',
    model: 'MOVE NVR-16 (Mx-S-NVR1A-16-POE)',
    category: 'nvr-recorder',
    tagline: '16-CH NVR with 16-port PoE switch and 4K camera support',
    verified: true,
    datasheetUrl: 'https://www.mobotix.com/en/MOVE-NVR',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 240,
      maxResolution: '4K',
      hddBays: 2,
      hddBayMaxTb: 14,
      poeInPorts: 16,
      poeBudgetW: 140,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '2× 1GbE RJ45 (WAN + LAN uplink)',
    },
  },

  {
    id: 'mobotix-move-nvr-64',
    manufacturer: 'Mobotix',
    model: 'MOVE NVR-64 (Mx-S-NVR1A-64-POE)',
    category: 'nvr-recorder',
    tagline: 'Rackmount 64-CH NVR with 24-port PoE+, 8 hot-swap RAID bays and failover',
    verified: true,
    datasheetUrl: 'https://www.mobotix.com/en/MOVE-NVR',
    specs: {
      channels: 64,
      inputBandwidthMbps: 480,
      outputBandwidthMbps: 540,
      maxResolution: '12 MP',
      hddBays: 8,
      poeInPorts: 24,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '10'],
      formFactor: '2U',
      networkPorts: '2× WAN + 2× LAN 1GbE RJ45',
    },
  },
];
